import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import { getRootSubdomain } from '../utils/subdomain';
import { useSilentWebsitePage } from './useSilentWebsitePage';
import { PUBLIC_BRAND_THEME } from './publicBrandTheme';
import { getStoredPublicSchoolProfile } from './publicSchoolProfile';

const defaultSocialLinks = [
  { label: 'Facebook', href: 'https://www.facebook.com/', icon: FacebookOutlinedIcon },
  { label: 'Instagram', href: 'https://www.instagram.com/', icon: InstagramIcon },
  { label: 'YouTube', href: 'https://www.youtube.com/', icon: YouTubeIcon },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/', icon: LinkedInIcon },
];

const defaultContactPhone = '+91 98765 43210';
const defaultContactEmail = 'admissions@greenfieldschool.edu.in';

const createDefaultHeaderConfig = (schoolName = 'Greenfield International School', publicProfile = {}) => ({
  brand: {
    logoSrc: publicProfile.logoUrl || '',
    logoAlt: `${schoolName} logo`,
    brandName: schoolName,
    slogan: publicProfile.slogan || 'Future-ready learning rooted in care, character, and confidence.',
    contactPhone: publicProfile.phone || defaultContactPhone,
    contactEmail: publicProfile.email || defaultContactEmail,
    socialLinks: [
      publicProfile.facebookLink ? { label: 'Facebook', href: publicProfile.facebookLink, icon: FacebookOutlinedIcon } : null,
      publicProfile.instagramLink ? { label: 'Instagram', href: publicProfile.instagramLink, icon: InstagramIcon } : null,
      publicProfile.twitterLink ? { label: 'Twitter', href: publicProfile.twitterLink, icon: TwitterIcon } : null,
      ...defaultSocialLinks,
    ].filter((item, index, arr) => item && arr.findIndex((entry) => entry?.label === item.label) === index),
    logoWidth: 42,
    logoHeight: 42,
    logoMobileWidth: 34,
    logoMobileHeight: 34,
    brandDisplayOption: { value: 'both' },
  },
  mainNav: [
    { label: 'Home', path: '/', enabled: true },
    { label: 'About', path: '/about', enabled: true },
    { label: 'Academics', path: '/academics', enabled: true },
    { label: 'Admissions', path: '/admissions', enabled: true },
    { label: 'Facilities', path: '/facilities', enabled: true },
    { label: 'News & Events', path: '/news', enabled: true },
    { label: 'Contact Us', path: '/contact', enabled: true },
  ],
  adminButton: {
    label: 'Apply Now',
    path: '/admissions',
  },
  utilityLinks: [],
  navFontSize: 12,
  showMainNav: true,
  notificationConfig: {
    enabled: false,
    notificationMessage: 'Admissions Open 2026-27',
    notificationDescription: 'Book a campus visit, meet our counsellors, and explore programmes across all grades.',
    actionButton: {
      enabled: true,
      label: 'Start Enquiry',
      path: '/admissions',
    },
  },
});

const RED_HEADER_THEME = PUBLIC_BRAND_THEME;

const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, numeric));
};

const parseContent = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch (_error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
};

const requiredMenuLinks = [];

const blockedMenuPaths = new Set([
  '/holidays',
  '/gallery',
  '/page/holidays',
  '/page/gallery',
  '/test',
  '/page/test',
  '/schedules',
  '/page/schedules',
  '/announcements',
  '/careers',
  '/results',
  '/page/admissions-overview',
  '/page/mission-and-vision',
]);

const pathAliasMap = {
  '/home': '/',
  '/page/home': '/',
  '/news': '/page/news',
  '/page/about': '/about',
  '/page/academics': '/academics',
  '/page/admissions': '/admissions',
  '/page/facilities': '/facilities',
  '/page/contact': '/contact',
};

const normalizeNavPath = (path = '') => {
  const trimmed = path.toString().trim();
  if (!trimmed) {
    return '';
  }
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const normalized = withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : '/';
  return pathAliasMap[normalized] || normalized;
};

const ensureRequiredNavLinks = (inputNav) => {
  const normalized = Array.isArray(inputNav)
    ? inputNav
      .map((item) => ({
        ...item,
        path: normalizeNavPath(item?.path || ''),
      }))
      .filter((item) => item?.label && item?.path && !blockedMenuPaths.has(item.path))
    : [];

  const deduped = [];
  const pathSet = new Set();

  normalized.forEach((item) => {
    if (!pathSet.has(item.path)) {
      pathSet.add(item.path);
      deduped.push(item);
    }
  });

  const merged = [...deduped];

  requiredMenuLinks.forEach((link) => {
    if (!pathSet.has(link.path)) {
      merged.push(link);
      pathSet.add(link.path);
    }
  });

  return merged;
};

const normalizeHeaderConfig = (payload, fallbackConfig = createDefaultHeaderConfig()) => {
  const parsed = parseContent(payload?.content ?? payload);
  const source = parsed?.header || parsed || {};

  return {
    ...fallbackConfig,
    ...source,
    brand: {
      ...fallbackConfig.brand,
      ...(source.brand || {}),
      brandDisplayOption: {
        ...fallbackConfig.brand.brandDisplayOption,
        ...(source.brand?.brandDisplayOption || {}),
      },
    },
    adminButton: {
      ...fallbackConfig.adminButton,
      ...(source.adminButton || {}),
    },
    notificationConfig: {
      ...fallbackConfig.notificationConfig,
      ...(source.notificationConfig || {}),
      actionButton: {
        ...fallbackConfig.notificationConfig.actionButton,
        ...(source.notificationConfig?.actionButton || {}),
      },
    },
    mainNav: ensureRequiredNavLinks(
      Array.isArray(source.mainNav) ? source.mainNav : fallbackConfig.mainNav,
    ),
  };
};

const WebHeader = () => {
  const tenantKey = getRootSubdomain();
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [publicProfile, setPublicProfile] = useState(() => getStoredPublicSchoolProfile());
  const fallbackSchoolName =
    publicProfile.name ||
    (typeof window !== 'undefined' && localStorage.getItem('schoolName')) ||
    'Greenfield International School';
  const fallbackHeaderConfig = useMemo(
    () => createDefaultHeaderConfig(fallbackSchoolName, publicProfile),
    [fallbackSchoolName, publicProfile]
  );

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const [bellPulse, setBellPulse] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(112);
  const [isCondensed, setIsCondensed] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const headerShellRef = useRef(null);
  const { data: headerConfig, loading } = useSilentWebsitePage({
    tenantKey,
    pageSlug: 'header_page',
    fallbackValue: fallbackHeaderConfig,
    normalize: normalizeHeaderConfig,
  });

  useEffect(() => {
    const syncPublicProfile = () => {
      setPublicProfile(getStoredPublicSchoolProfile());
    };

    window.addEventListener('storage', syncPublicProfile);
    window.addEventListener('public-school-profile-updated', syncPublicProfile);
    return () => {
      window.removeEventListener('storage', syncPublicProfile);
      window.removeEventListener('public-school-profile-updated', syncPublicProfile);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsCondensed(window.scrollY > 36);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const element = headerShellRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = Math.ceil(element.getBoundingClientRect().height);
      if (nextHeight > 0) {
        setHeaderHeight(nextHeight);
      }
    };

    updateHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(element);
    } else {
      window.addEventListener('resize', updateHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateHeight);
      }
    };
  }, []);

  const { notificationConfig } = headerConfig;

  useEffect(() => {
    if (!notificationConfig?.enabled) {
      return;
    }

    const noticeKey = `header-notice-${tenantKey}`;
    if (sessionStorage.getItem(noticeKey)) {
      return;
    }

    const timer = setTimeout(() => {
      setShowNoticeDialog(true);
      setBellPulse(true);
      sessionStorage.setItem(noticeKey, '1');
    }, 1800);

    return () => clearTimeout(timer);
  }, [notificationConfig?.enabled, tenantKey]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleCloseNotice = useCallback(() => {
    setShowNoticeDialog(false);
    setBellPulse(false);
  }, []);

  const handleOpenNotice = useCallback(() => {
    setShowNoticeDialog(true);
    setBellPulse(false);
  }, []);

  const handleOpenMoreMenu = useCallback((event) => {
    setMoreAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMoreMenu = useCallback(() => {
    setMoreAnchorEl(null);
  }, []);

  const {
    brand,
    mainNav,
    adminButton,
    navFontSize,
    showMainNav,
    notificationConfig: notice,
  } = headerConfig;
  const showNoticeButton = Boolean(notice?.enabled);

  const activeNavLinks = useMemo(
    () => (
      Array.isArray(mainNav)
        ? mainNav.filter((item) => {
          if (item?.enabled === false || !item?.label || !item?.path) {
            return false;
          }
          return !blockedMenuPaths.has(normalizeNavPath(item.path));
        })
        : []
    ),
    [mainNav],
  );

  const primaryNavLinks = useMemo(() => activeNavLinks.slice(0, 7), [activeNavLinks]);
  const overflowNavLinks = useMemo(() => activeNavLinks.slice(7), [activeNavLinks]);
  const hasOverflowNav = overflowNavLinks.length > 0;
  const contactPhone = brand?.contactPhone || defaultContactPhone;
  const contactEmail = brand?.contactEmail || defaultContactEmail;
  const socialLinks = Array.isArray(brand?.socialLinks) && brand.socialLinks.length
    ? brand.socialLinks
    : defaultSocialLinks;
  const hasUtilityContent = Boolean(contactPhone || contactEmail || socialLinks.length);
  const hasBrandContent = Boolean(brand?.logoSrc || brand?.brandName || brand?.slogan);
  const hasAdminButton = Boolean(adminButton?.label && adminButton?.path);
  const hasAnyHeaderContent = hasBrandContent || (showMainNav && activeNavLinks.length > 0) || hasUtilityContent || hasAdminButton || showNoticeButton;

  const brandDisplay = brand?.brandDisplayOption?.value || 'both';
  const logoDesktopWidth = clampNumber(
    brand?.logoWidth ?? brand?.logoSize,
    24,
    112,
    fallbackHeaderConfig.brand.logoWidth
  );
  const logoDesktopHeight = clampNumber(
    brand?.logoHeight ?? brand?.logoSize,
    24,
    112,
    fallbackHeaderConfig.brand.logoHeight
  );
  const logoMobileWidth = clampNumber(
    brand?.logoMobileWidth,
    24,
    112,
    fallbackHeaderConfig.brand.logoMobileWidth
  );
  const logoMobileHeight = clampNumber(
    brand?.logoMobileHeight,
    24,
    112,
    fallbackHeaderConfig.brand.logoMobileHeight
  );

  const renderBrand = (compact = false, tone = 'header') => {
    if (!hasBrandContent) {
      return null;
    }
    const showLogo = brandDisplay !== 'nameOnly';
    const showName = brandDisplay !== 'logoOnly';
    const logoWidth = compact ? logoMobileWidth : logoDesktopWidth;
    const logoHeight = compact ? logoMobileHeight : logoDesktopHeight;
    const isLightTone = tone === 'header';

    return (
      <Box
        component={RouterLink}
        to="/"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 0.65 : 0.95,
          textDecoration: 'none',
          minWidth: 0,
          transition: 'opacity 0.2s ease',
          '&:hover': {
            opacity: 0.92,
          },
        }}
      >
        {showLogo && brand?.logoSrc && (
          <Box
            sx={{
              width: logoWidth,
              height: logoHeight,
              p: 0.22,
                border: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.14)}`,
                backgroundColor: '#FFFFFF',
                flexShrink: 0,
                boxShadow: '0 8px 18px rgba(13, 31, 73, 0.08)',
              }}
          >
            <Box
              component="img"
              src={brand.logoSrc}
              alt={brand.logoAlt || 'School logo'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: '#FFFFFF',
              }}
            />
          </Box>
        )}

        {showName && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: '"Playfair Display", "Montserrat", serif',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                fontSize: compact ? '0.9rem' : isCondensed ? '0.96rem' : '1.05rem',
                textTransform: 'none',
                color: isLightTone ? '#FFFFFF' : RED_HEADER_THEME.primaryDark,
                lineHeight: 1.15,
              }}
            >
              {brand?.brandName || ''}
            </Typography>
            {!!brand?.slogan && !compact && (
              <Typography
                noWrap
                variant="caption"
                sx={{
                  color: isLightTone ? alpha('#FFFFFF', 0.78) : alpha(RED_HEADER_THEME.text, 0.7),
                  display: 'block',
                  maxWidth: 240,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'none',
                }}
              >
                {brand.slogan}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderNavLinks = (forMobile = false) => {
    if (!showMainNav || !activeNavLinks.length) {
      return null;
    }
    const currentPath = normalizeNavPath(location.pathname);
    const navItems = forMobile ? activeNavLinks : primaryNavLinks;

    return (
      <Stack
        direction={forMobile ? 'column' : 'row'}
        spacing={forMobile ? 0.55 : 0.25}
        sx={{
          width: forMobile ? '100%' : 'auto',
          alignItems: forMobile ? 'stretch' : 'center',
          px: forMobile ? 0 : 0,
          py: forMobile ? 0 : 0,
          flexWrap: 'nowrap',
        }}
      >
        {navItems.map((item) => {
          const isActive = currentPath === item.path;

          return (
            <MuiLink
              key={`${item.label}-${item.path}`}
              component={RouterLink}
              to={item.path}
              onClick={() => forMobile && setMobileOpen(false)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: forMobile ? 'flex-start' : 'center',
                px: forMobile ? 0.9 : 0.85,
                py: forMobile ? 0.88 : isCondensed ? 0.55 : 0.75,
                textDecoration: 'none',
                color: isActive ? '#FFFFFF' : alpha('#FFFFFF', 0.9),
                fontSize: `${Math.max(11, navFontSize)}px`,
                fontWeight: isActive ? 700 : 600,
                letterSpacing: '0.06em',
                textTransform: forMobile ? 'none' : 'uppercase',
                position: 'relative',
                borderRadius: 0,
                backgroundColor: 'transparent',
                borderBottom: forMobile
                  ? `1px solid ${alpha(RED_HEADER_THEME.primary, 0.12)}`
                  : `3px solid ${isActive ? '#FFFFFF' : 'transparent'}`,
                transition: 'color 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
                '&:hover': {
                  color: forMobile ? RED_HEADER_THEME.accent : '#FFFFFF',
                  backgroundColor: forMobile ? alpha(RED_HEADER_THEME.primary, 0.03) : 'transparent',
                  transform: forMobile ? 'none' : 'translateY(-1px)',
                },
              }}
            >
              {item.label}
            </MuiLink>
          );
        })}
        {!forMobile && hasOverflowNav && (
          <Button
            onClick={handleOpenMoreMenu}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16, transform: moreAnchorEl ? 'rotate(180deg)' : 'none', transition: 'transform 180ms ease' }} />}
            sx={{
              px: 0.85,
              py: isCondensed ? 0.55 : 0.75,
              minWidth: 'auto',
              color: alpha('#FFFFFF', 0.9),
              fontSize: `${Math.max(11, navFontSize)}px`,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderRadius: 0,
              borderBottom: `3px solid ${moreAnchorEl ? '#FFFFFF' : 'transparent'}`,
            }}
          >
            More
          </Button>
        )}
      </Stack>
    );
  };

  const renderUtilityLinks = (forMobile = false) => {
    if (!hasUtilityContent) {
      return null;
    }
    return (
      <Stack
        direction={forMobile ? 'column' : 'row'}
        spacing={forMobile ? 1.1 : 1.8}
        alignItems={forMobile ? 'stretch' : 'center'}
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <Stack direction={forMobile ? 'column' : 'row'} spacing={forMobile ? 0.9 : 1.8} alignItems={forMobile ? 'stretch' : 'center'}>
          {contactPhone ? (
          <Stack direction="row" spacing={0.8} alignItems="center">
            <CallOutlinedIcon sx={{ fontSize: 16, color: forMobile ? RED_HEADER_THEME.primary : alpha('#FFFFFF', 0.8) }} />
            <MuiLink
              href={`tel:${contactPhone.replace(/\s+/g, '')}`}
              underline="none"
              sx={{
                color: forMobile ? RED_HEADER_THEME.primaryDark : alpha('#FFFFFF', 0.9),
                fontSize: forMobile ? '0.9rem' : '0.76rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}
            >
              {contactPhone}
            </MuiLink>
          </Stack>
          ) : null}
          {contactEmail ? (
          <Stack direction="row" spacing={0.8} alignItems="center">
            <MailOutlineIcon sx={{ fontSize: 16, color: forMobile ? RED_HEADER_THEME.primary : alpha('#FFFFFF', 0.8) }} />
            <MuiLink
              href={`mailto:${contactEmail}`}
              underline="none"
              sx={{
                color: forMobile ? RED_HEADER_THEME.primaryDark : alpha('#FFFFFF', 0.9),
                fontSize: forMobile ? '0.9rem' : '0.76rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
              }}
            >
              {contactEmail}
            </MuiLink>
          </Stack>
          ) : null}
        </Stack>
        {socialLinks.length ? (
        <Stack direction="row" spacing={forMobile ? 0.5 : 0.35} alignItems="center">
          {socialLinks.map((item) => {
            const Icon = item.icon;
            return (
              <IconButton
                key={item.label}
                component="a"
                href={item.href}
                target="_blank"
                rel="noreferrer"
                size="small"
                sx={{
                  width: 30,
                  height: 30,
                  border: forMobile ? `1px solid ${alpha(RED_HEADER_THEME.primary, 0.16)}` : `1px solid ${alpha('#FFFFFF', 0.16)}`,
                  borderRadius: 0,
                  color: forMobile ? RED_HEADER_THEME.primaryDark : '#FFFFFF',
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
              </IconButton>
            );
          })}
        </Stack>
        ) : null}
      </Stack>
    );
  };

  const adminPath = adminButton?.path || '';
  const adminLabel = adminButton?.label || '';

  return (
    <>
      <>
        <Box
          ref={headerShellRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            width: 'auto',
            zIndex: theme.zIndex.drawer + 10,
          }}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={{
              borderBottom: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.08)}`,
              bgcolor: RED_HEADER_THEME.primary,
              backgroundImage: RED_HEADER_THEME.headerGradient,
              boxShadow: isCondensed ? '0 8px 24px rgba(15, 23, 42, 0.16)' : '0 10px 28px rgba(15, 23, 42, 0.1)',
              backdropFilter: 'none',
              transition: 'box-shadow 220ms ease, background-color 220ms ease, transform 220ms ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 1,
                backgroundColor: alpha(RED_HEADER_THEME.primary, 0.1),
                opacity: 1,
              },
            }}
          >
        {!isMobile && hasUtilityContent && (
          <Box
            sx={{
              height: isCondensed ? 0 : 36,
              overflow: 'hidden',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              transition: 'height 240ms ease, opacity 240ms ease, border-color 240ms ease',
              opacity: isCondensed ? 0 : 1,
              backgroundColor: alpha('#081428', 0.18),
            }}
          >
            <Container
              maxWidth="xl"
              sx={{
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {renderUtilityLinks(false)}
            </Container>
          </Box>
        )}
        <Toolbar
          sx={{
            minHeight: { xs: 60, md: isCondensed ? 58 : 78 },
            px: 0,
            borderTop: isCondensed ? '1px solid rgba(255,255,255,0.05)' : '1px solid transparent',
            transition: 'min-height 240ms ease, border-color 240ms ease',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.15,
              minHeight: { xs: 60, md: isCondensed ? 58 : 78 },
              py: { xs: 0.35, md: isCondensed ? 0.05 : 0.4 },
              transition: 'min-height 240ms ease, padding 240ms ease',
            }}
          >
            <Box sx={{ minWidth: 0 }}>{renderBrand(isMobile, 'header')}</Box>

            {!isMobile && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  minWidth: 0,
                  px: 1.2,
                }}
              >
                <Box
                  sx={{
                    p: 0,
                    transform: isCondensed ? 'translateY(-1px)' : 'none',
                    transition: 'transform 220ms ease',
                  }}
                >
                  {renderNavLinks(false)}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={0.65} alignItems="center">
              {showNoticeButton && (
                <IconButton
                  size="small"
                  onClick={handleOpenNotice}
                  sx={{
                    border: `1px solid ${alpha('#FFFFFF', 0.28)}`,
                    borderRadius: 0,
                    color: '#FFFFFF',
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: alpha('#FFFFFF', 0.08),
                    },
                  }}
                >
                  <Badge color="error" variant="dot" invisible={!notice?.enabled && !bellPulse}>
                    {showNoticeDialog || bellPulse ? (
                      <NotificationsActiveIcon fontSize="small" sx={{ color: RED_HEADER_THEME.accent }} />
                    ) : (
                      <NotificationImportantIcon fontSize="small" />
                    )}
                  </Badge>
                </IconButton>
              )}

              {!isMobile && hasAdminButton && (
                <Button
                  size="small"
                  variant="contained"
                  component={RouterLink}
                  to={adminPath}
                  endIcon={<ArrowOutwardIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    borderRadius: 0,
                    px: isCondensed ? 1.15 : 1.55,
                    borderColor: '#FFFFFF',
                    color: RED_HEADER_THEME.primaryDark,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    bgcolor: '#FFFFFF',
                    background: '#FFFFFF',
                    boxShadow: 'none',
                    minHeight: isCondensed ? 34 : 38,
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      bgcolor: '#F8FAFC',
                      background: '#F8FAFC',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {adminLabel}
                </Button>
              )}

              {isMobile && (showMainNav && activeNavLinks.length > 0 || hasAdminButton || hasUtilityContent || hasBrandContent) && (
                <IconButton
                  size="small"
                  onClick={handleDrawerToggle}
                  sx={{
                    border: `1px solid ${alpha('#FFFFFF', 0.28)}`,
                    borderRadius: 0,
                    color: '#FFFFFF',
                    bgcolor: 'transparent',
                  }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Container>
        </Toolbar>
          </AppBar>
        </Box>
        <Box sx={{ height: `${headerHeight}px`, width: '100%' }} />
      </>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: 360,
            borderRight: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.14)}`,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            boxShadow: 'none',
            p: 1.4,
            bgcolor: RED_HEADER_THEME.surface,
            backgroundImage: 'none',
          },
        }}
      >
        <Stack spacing={1.2} sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {renderBrand(false, 'drawer')}
            <IconButton
              size="small"
              onClick={handleDrawerToggle}
              sx={{ border: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.24)}`, borderRadius: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {showMainNav && activeNavLinks.length > 0 ? (
            <>
              <Typography variant="caption" sx={{ color: alpha(RED_HEADER_THEME.text, 0.7), fontWeight: 700, letterSpacing: '0.08em' }}>
                NAVIGATION
              </Typography>
              <Divider />
            </>
          ) : null}
          {hasUtilityContent ? (
            <>
              <Typography variant="caption" sx={{ color: alpha(RED_HEADER_THEME.text, 0.7), fontWeight: 700, letterSpacing: '0.08em' }}>
                CONTACT
              </Typography>
              <Box>{renderUtilityLinks(true)}</Box>
              <Divider />
            </>
          ) : null}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.2, pt: 0.3 }}>{renderNavLinks(true)}</Box>
          {(showNoticeButton || hasAdminButton) ? <Divider /> : null}

          <Stack spacing={0.8}>
            {showNoticeButton ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                handleDrawerToggle();
                handleOpenNotice();
              }}
              sx={{
                borderColor: alpha(RED_HEADER_THEME.primary, 0.34),
                color: RED_HEADER_THEME.primary,
                bgcolor: alpha('#FFFFFF', 0.86),
                '&:hover': { borderColor: RED_HEADER_THEME.primary, bgcolor: alpha('#FFFFFF', 0.96) },
              }}
            >
              Notices
            </Button>
            ) : null}
            {hasAdminButton ? (
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={adminPath}
              fullWidth
              endIcon={<ArrowOutwardIcon sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: 0,
                background: RED_HEADER_THEME.buttonGradient || RED_HEADER_THEME.primary,
                boxShadow: 'none',
                '&:hover': {
                  background: RED_HEADER_THEME.buttonGradientHover || RED_HEADER_THEME.primaryDark,
                  boxShadow: 'none',
                },
              }}
            >
              {adminLabel}
            </Button>
            ) : null}
          </Stack>
        </Stack>
      </Drawer>

      <Dialog
        open={showNoticeDialog && notice?.enabled}
        onClose={handleCloseNotice}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            border: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.24)}`,
            backgroundImage: 'none',
            backgroundColor: '#FFFFFF',
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1.2,
            fontFamily: '"Yantramanav", "Roboto", sans-serif',
            fontWeight: 760,
            fontSize: '1rem',
            bgcolor: RED_HEADER_THEME.primary,
            backgroundImage: 'none',
            color: '#F8FAFF',
          }}
        >
          {notice?.notificationMessage || 'Important Notice'}
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
            {notice?.notificationDescription || 'Please review the latest announcement from the school.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" variant="text" onClick={handleCloseNotice} sx={{ borderRadius: 0 }}>
            Close
          </Button>
          {notice?.actionButton?.enabled && notice?.actionButton?.path && notice?.actionButton?.label && (
            <Button size="small" variant="contained" component={RouterLink} to={notice.actionButton.path} onClick={handleCloseNotice} sx={{ borderRadius: 0 }}>
              {notice.actionButton.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={180}
        slotProps={{
          paper: {
            sx: {
              mt: 1.2,
              minWidth: 260,
              borderRadius: 0,
              border: `1px solid ${alpha(RED_HEADER_THEME.primary, 0.12)}`,
              boxShadow: '0 14px 34px rgba(15, 23, 42, 0.14)',
            },
          },
        }}
      >
        {overflowNavLinks.map((item) => (
          <MenuItem
            key={`overflow-${item.label}-${item.path}`}
            component={RouterLink}
            to={item.path}
            onClick={handleCloseMoreMenu}
            sx={{
              minHeight: 42,
              fontSize: '0.82rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default WebHeader;
